import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import { lazy, Suspense } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CartProvider } from "./contexts/CartContext";

// Critical path — load eagerly (above the fold on first visit)
import Home from "./pages/Home";

// Non-critical routes — lazy load to reduce initial bundle
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Departments = lazy(() => import("./pages/Departments"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const BundleBuilder = lazy(() => import("./pages/BundleBuilder"));
const AgentProfile = lazy(() => import("./pages/AgentProfile"));
const TalentMatchmaker = lazy(() => import("./pages/TalentMatchmaker"));
const MyDownloads = lazy(() => import("./pages/MyDownloads"));
const ComingSoonLazy = lazy(() => import("./pages/ComingSoon").then(m => ({ default: m.default })));
const AIPlaybooks = lazy(() => import("./pages/ComingSoon").then(m => ({ default: m.AIPlaybooks })));
const PromptPacks = lazy(() => import("./pages/ComingSoon").then(m => ({ default: m.PromptPacks })));
const AutomationTemplates = lazy(() => import("./pages/ComingSoon").then(m => ({ default: m.AutomationTemplates })));
const AITrainingKits = lazy(() => import("./pages/ComingSoon").then(m => ({ default: m.AITrainingKits })));

// Minimal fallback spinner that matches the cyberpunk theme
function PageLoader() {
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--cyber-black, #070912)',
    }}>
      <div style={{
        width: 40,
        height: 40,
        border: '2px solid rgba(0,166,235,0.15)',
        borderTop: '2px solid #00A6EB',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path={"/"} component={Home} />
        <Route path={"/terms"} component={TermsOfService} />
        <Route path={"/privacy"} component={PrivacyPolicy} />
        <Route path={"/departments"} component={Departments} />
        <Route path={"/order-success"} component={OrderSuccess} />
        <Route path={"/bundle-builder"} component={BundleBuilder} />
        <Route path={"/agent/:id"} component={AgentProfile} />
        <Route path={"/matchmaker"} component={TalentMatchmaker} />
        <Route path={"/playbooks"} component={AIPlaybooks} />
        <Route path={"/prompt-packs"} component={PromptPacks} />
        <Route path={"/automation-templates"} component={AutomationTemplates} />
        <Route path={"/ai-training"} component={AITrainingKits} />
        <Route path={"/my-downloads"} component={MyDownloads} />
        <Route path={"/404"} component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <CartProvider>
          <TooltipProvider>
            <Toaster
              toastOptions={{
                style: {
                  background: 'rgba(13, 17, 35, 0.95)',
                  border: '1px solid rgba(0, 166, 235, 0.3)',
                  color: '#E8F4FF',
                  fontFamily: "'Rajdhani', sans-serif",
                },
              }}
            />
            <main>
              <Router />
            </main>
          </TooltipProvider>
        </CartProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
